import { useCallback, useEffect, useRef, useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import type { LocationItem, LocationItemTypeEnum } from 'src/app/pages/search/shared/types';

import { useClearSelectedLocation } from './hooks/useClearSelectedLocation';
import { useLocationSearchCities } from './hooks/useLocationSearchCities';
import { useLocationSearchCountries } from './hooks/useLocationSearchCountries';
import { useSelectLocation } from './hooks/useSelectLocation';

type Result = ModelHookResult<
	{
		locations: readonly LocationItem[];
		isLoading: boolean;
		isError: boolean;
		selectedLocation: string | undefined;
	},
	{
		searchDebounced: (value: string) => void;
		onResetQuery: () => void;
		selectLocation: (value: string, type: LocationItemTypeEnum) => void;
	}
>;

// eslint-disable-next-line max-lines-per-function
export function useLocationSearch(): Result {
	// THIS STATE IS USED TO DEBOUNCE THE QUERY CALL
	const [queryLocation, setQueryLocation] = useState('');

	const {
		actions: { searchForCities },
	} = useLocationSearchCities();

	const {
		actions: { searchForCountries },
	} = useLocationSearchCountries();

	const {
		actions: { selectLocation },
	} = useSelectLocation({ updateQueryLocation: setQueryLocation });

	const {
		data: { selectedLocation },
		actions: { clearSelectedLocation },
	} = useClearSelectedLocation();

	const {
		data: locations,
		isLoading,
		isError,
	} = useQuery({
		queryKey: ['search-location-city-2', queryLocation, selectedLocation],
		queryFn: async () => {
			const [cities, countries] = await Promise.all([
				searchForCities(queryLocation, selectedLocation),
				searchForCountries(queryLocation, selectedLocation),
			]);

			return [...cities, ...countries];
		},
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
	});

	const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

	// clean up timeout on unmount
	useEffect(() => {
		return () => {
			if (debounceTimeout.current) {
				clearTimeout(debounceTimeout.current);
			}
		};
	}, []);

	return {
		data: {
			locations: locations || [],
			isLoading,
			isError,
			selectedLocation,
		},
		actions: {
			searchDebounced: useCallback((value: string) => {
				if (debounceTimeout.current) {
					clearTimeout(debounceTimeout.current);
				}
				debounceTimeout.current = setTimeout(() => {
					setQueryLocation(value);
				}, 300);
			}, []),
			onResetQuery: useCallback(() => {
				setQueryLocation('');
				clearSelectedLocation();
			}, [clearSelectedLocation]),
			selectLocation,
		},
	};
}
